/**
 * Éditeur des contenus du Grand Quiz du Lair : une liste de « contenus » par épreuve
 * (le premier prêt est celui qui sera joué), formulaire généré depuis le schéma de l'épreuve.
 */
import React, { useEffect, useRef, useState } from 'react';
import { BY_ID, FORMATS, HOSTS, media } from './quizShowFormats';
import { AvatarModal, Portrait } from './QuizShowAvatars';
import { LyricsField } from './QuizShowKaraoke';
import { FormatLogo } from './QuizShowParts';

const clone = (x) => JSON.parse(JSON.stringify(x));

function MediaField({ value, onChange, api, accept, label }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try { const { url } = await api.upload(file); onChange(url); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const isAudio = accept.startsWith('audio');
  return (
    <div className="qse-media" onPaste={(e) => { const f = [...(e.clipboardData?.files || [])][0]; if (f) { e.preventDefault(); send(f); } }}>
      <input type="text" value={value || ''} placeholder={isAudio ? 'Lien YouTube ou fichier son' : 'Lien de l’image, ou colle / glisse une image'} onChange={(e) => onChange(e.target.value)} />
      <label className="qse-up">{busy ? '…' : '⬆'}<input type="file" accept={accept} onChange={(e) => send(e.target.files[0])} hidden /></label>
      {value && !isAudio && <img src={media(value)} alt={label} className="qse-thumb" />}
      {value && isAudio && !/youtu/.test(value) && <audio src={media(value)} controls className="qse-audio" />}
      {err && <span className="qse-err">{err}</span>}
    </div>
  );
}

function PointField({ value, onChange, mapUrl }) {
  if (!mapUrl) return <p className="qse-note">Ajoute d’abord la carte du monde en haut du formulaire.</p>;
  return (
    <div className="qse-point" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); onChange({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }); }} role="presentation">
      <img src={media(mapUrl)} alt="Carte" draggable="false" />
      {value && <span className="qs-pin is-true" style={{ left: `${value.x * 100}%`, top: `${value.y * 100}%` }} />}
    </div>
  );
}

// Bon match : on choisit les deux cases de chaque point commun en cliquant dans la grille
const PAIR_COLORS = ['#ff7ab6', '#5cf2ff', '#ffd84d', '#a3ff5c', '#c084fc', '#ff9a3c'];
function PairsField({ value, onChange, items }) {
  const [active, setActive] = useState(0);
  const links = value || [];
  const owner = {};
  links.forEach((l, k) => { [l.a, l.b].filter(Boolean).forEach((c) => { (owner[c] = owner[c] || []).push(k); }); });
  const set = (k, patch) => { const n = links.map((l, j) => (j === k ? { ...l, ...patch } : l)); onChange(n); };
  const pickTile = (c) => {
    const l = links[active] || {};
    if (l.a === c) return set(active, { a: l.b || 0, b: 0, checked: false });
    if (l.b === c) return set(active, { b: 0, checked: false });
    if (!l.a) return set(active, { a: c, checked: false });
    if (!l.b) return set(active, { b: c, checked: false });
    return set(active, { b: c, checked: false });
  };
  const thumb = (c) => (c ? <span className="qse-pthumb">{items[c - 1]?.image ? <img src={media(items[c - 1].image)} alt="" /> : null}<b>n°{c}</b> {items[c - 1]?.label || ''}</span> : <span className="qse-pthumb is-empty">?</span>);
  return (
    <div className="qse-pairs">
      <div className="qse-plist">
        {links.map((l, k) => (
          <div key={k} className={`qse-pair${k === active ? ' is-on' : ''}`} style={{ '--pc': PAIR_COLORS[k % 6] }} onClick={() => setActive(k)} role="presentation">
            <span className="qse-pdot">{k + 1}</span>
            <input value={l.trait || ''} placeholder="Point commun (ex. A perdu un bras)" onChange={(e) => set(k, { trait: e.target.value, checked: false })} />
            {thumb(l.a)}<span>+</span>{thumb(l.b)}
            <label className={`qse-check${l.checked ? ' is-ok' : ''}`} title="Coche quand tu as vérifié qu’aucune autre case ne partage ce point commun"><input type="checkbox" checked={Boolean(l.checked)} onChange={(e) => set(k, { checked: e.target.checked })} /> Vérifié</label>
          </div>
        ))}
      </div>
      <p className="qse-note">Point commun n°{active + 1} sélectionné : clique sur ses deux cases. Les cases en couleur sont déjà prises. Avant de cocher « Vérifié », relis les 23 autres cases : aucune ne doit partager ce point commun.</p>
      <div className="qse-pgrid">
        {items.map((it, k) => {
          const c = k + 1; const o = owner[c] || [];
          return (
            <button type="button" key={k} className={`qse-ptile${o.length > 1 ? ' is-dup' : ''}`} style={o.length ? { '--pc': PAIR_COLORS[o[0] % 6] } : null} onClick={() => pickTile(c)}>
              <i>{c}</i>{it.image ? <img src={media(it.image)} alt="" /> : <span className="qse-noimg">pas d’image</span>}<b>{it.label || '—'}</b>
              {o.length > 0 && <em>{o.map((x) => x + 1).join(' · ')}</em>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ f, value, onChange, sib, root, api }) {
  const set = (v) => onChange(v);
  switch (f.t) {
    case 'textarea': return <textarea value={value || ''} rows={2} onChange={(e) => set(e.target.value)} />;
    case 'number': return <input type="number" value={value ?? ''} onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))} />;
    case 'bool': return <label className="qse-bool"><input type="checkbox" checked={Boolean(value)} onChange={(e) => set(e.target.checked)} /> {f.label}</label>;
    case 'image': return <MediaField value={value} onChange={set} api={api} accept="image/*" label={f.label} />;
    case 'audio': return <MediaField value={value} onChange={set} api={api} accept="audio/*" label={f.label} />;
    case 'point': return <PointField value={value} onChange={set} mapUrl={root[f.map]} />;
    case 'select': return <select value={value || ''} onChange={(e) => set(e.target.value)}>{f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
    case 'index': return (
      <select value={value ?? 0} onChange={(e) => set(Number(e.target.value))}>
        {(sib[f.of] || []).map((c, k) => <option key={k} value={k}>{String.fromCharCode(65 + k)} · {c.text || (c.image ? '(image)' : '(vide)')}</option>)}
      </select>
    );
    case 'list': return <ListField f={f} value={value || []} onChange={set} root={root} api={api} />;
    case 'pairs': return <PairsField value={value} onChange={set} items={root[f.items] || []} />;
    case 'lyrics': return <LyricsField value={value} onChange={set} root={root} />;
    default: return <input type="text" value={value || ''} onChange={(e) => set(e.target.value)} />;
  }
}

function Group({ fields, value, onChange, root, api }) {
  return (
    <div className="qse-group">
      {fields.map((f) => (
        <div key={f.k} className={`qse-field t-${f.t}${f.required && !value[f.k] ? ' is-missing' : ''}`}>
          {f.t !== 'bool' && <span className="qse-label">{f.label}</span>}
          <Field f={f} value={value[f.k]} sib={value} root={root} api={api} onChange={(v) => onChange({ ...value, [f.k]: v })} />
        </div>
      ))}
    </div>
  );
}

function ListField({ f, value, onChange, root, api }) {
  const compact = f.fields.length <= 3 && value.length > 6;
  const blankItem = () => Object.fromEntries(f.fields.map((x) => [x.k, x.t === 'list' ? [{ text: '' }, { text: '' }] : x.t === 'number' ? 0 : x.t === 'bool' ? false : '']));
  return (
    <div className={`qse-list${compact ? ' is-compact' : ''}`}>
      {value.map((item, k) => (
        <div key={k} className="qse-item">
          <div className="qse-item-head">
            <b>{f.label === 'Choix' ? String.fromCharCode(65 + k) : `n°${k + 1}`}</b>
            {value.length > (f.min || 0) && <button type="button" onClick={() => onChange(value.filter((_, j) => j !== k))}>Retirer</button>}
          </div>
          <Group fields={f.fields} value={item} root={root} api={api} onChange={(v) => { const n = [...value]; n[k] = v; onChange(n); }} />
        </div>
      ))}
      {value.length < (f.max || 99) && <button type="button" className="qse-add" onClick={() => onChange([...value, blankItem()])}>+ Ajouter</button>}
    </div>
  );
}

// Enregistrement de la voix off directement dans le navigateur (micro), avec le texte en prompteur
function VoiceRecorder({ api, script, onDone }) {
  const [state, setState] = useState('idle'); // idle | rec | ready | sending
  const [secs, setSecs] = useState(0);
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const rec = useRef(null); const chunks = useRef([]); const blob = useRef(null); const timer = useRef(0); const stream = useRef(null);
  useEffect(() => () => { clearInterval(timer.current); if (stream.current) stream.current.getTracks().forEach((t) => t.stop()); }, []);
  const start = async () => {
    setErr('');
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || '';
      const r = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        const b = new Blob(chunks.current, { type: r.mimeType || 'audio/webm' }); blob.current = b;
        setUrl(URL.createObjectURL(b)); setState('ready');
        stream.current.getTracks().forEach((t) => t.stop()); stream.current = null;
      };
      rec.current = r; r.start(); setSecs(0); setState('rec');
      const t0 = Date.now(); timer.current = setInterval(() => setSecs(Math.floor((Date.now() - t0) / 1000)), 250);
    } catch (e) { setErr('Micro inaccessible : autorise le micro pour ce site dans ton navigateur.'); }
  };
  const stop = () => { clearInterval(timer.current); if (rec.current && rec.current.state !== 'inactive') rec.current.stop(); };
  const use = async () => {
    setState('sending');
    try {
      const ext = (blob.current.type.includes('mp4') ? 'm4a' : blob.current.type.includes('ogg') ? 'ogg' : 'webm');
      const file = new File([blob.current], `voix-off.${ext}`, { type: blob.current.type.split(';')[0] });
      const { url: u } = await api.upload(file); onDone(u); setState('idle'); setUrl('');
    } catch (e) { setErr(e.message); setState('ready'); }
  };
  return (
    <div className={`qse-rec is-${state}`}>
      {state === 'rec' && <div className="qse-prompter"><small>Lis ce texte</small><p>{script}</p></div>}
      <div className="qse-rec-bar">
        {state === 'idle' && <button type="button" className="qs-btn is-red" onClick={start}>🎙️ Enregistrer ma voix</button>}
        {state === 'rec' && <><span className="qse-rec-dot" />{`${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`}<button type="button" className="qs-btn" onClick={stop}>■ Stop</button></>}
        {state === 'ready' && <><audio src={url} controls /><button type="button" className="qs-btn is-gold" onClick={use}>Utiliser cet enregistrement</button><button type="button" className="qs-btn is-ghost" onClick={start}>Recommencer</button></>}
        {state === 'sending' && <span>Envoi…</span>}
      </div>
      {err && <p className="qse-err">{err}</p>}
    </div>
  );
}

// Présentateur de l'épreuve : personnage, texte de la bulle, voix off
function HostPanel({ api, fmt }) {
  const def = HOSTS[fmt] || {};
  const [cfg, setCfg] = useState(null);
  const [picking, setPicking] = useState(false);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let live = true; setCfg(null); setMsg('');
    api.get('/config').then(({ formats }) => { if (live) setCfg({ host: '', name: '', line: '', voice: '', ...((formats || {})[fmt] || {}) }); }).catch(() => live && setCfg({ host: '', name: '', line: '', voice: '' }));
    return () => { live = false; };
  }, [api, fmt]);
  if (!cfg) return null;
  const host = cfg.host || def.host; const name = cfg.name || def.name;
  const save = async () => { setMsg('…'); try { await api.put(`/config/${fmt}`, cfg); setMsg('Présentateur enregistré ✓'); } catch (e) { setMsg(e.message); } };
  const listen = () => { if (preview) { preview.pause(); setPreview(null); return; } if (!cfg.voice) return; const a = new Audio(media(cfg.voice)); a.onended = () => setPreview(null); a.play(); setPreview(a); };
  return (
    <div className="qse-host">
      <h3>Présentateur de l’épreuve</h3>
      <div className="qse-host-row">
        <button type="button" className="qse-host-pick" onClick={() => setPicking(true)} title="Changer de personnage"><Portrait v={host} name={name} /><em>Changer</em></button>
        <div className="qse-host-fields">
          <label><span className="qse-label">Nom affiché</span><input value={cfg.name} placeholder={def.name} onChange={(e) => setCfg({ ...cfg, name: e.target.value })} /></label>
          <label><span className="qse-label">Texte de la bulle (vide = texte par défaut)</span><textarea rows={4} value={cfg.line} placeholder={def.line} onChange={(e) => setCfg({ ...cfg, line: e.target.value })} /></label>
          <label><span className="qse-label">Voix off (fichier son, facultatif)</span><MediaField value={cfg.voice} onChange={(v) => setCfg({ ...cfg, voice: v })} api={api} accept="audio/*" label="Voix off" /></label>
          <VoiceRecorder api={api} script={cfg.line || def.line} onDone={(u) => { setCfg({ ...cfg, voice: u }); setMsg('Voix enregistrée : clique sur « Enregistrer le présentateur »'); }} />
          <div className="qse-host-actions">
            <button type="button" className="qs-btn is-gold" onClick={save}>Enregistrer le présentateur</button>
            {cfg.voice && <button type="button" className="qs-btn is-ghost" onClick={listen}>{preview ? '■ Stop' : '▶ Écouter la voix'}</button>}
            {(cfg.host || cfg.name || cfg.line) && <button type="button" className="qs-btn is-ghost" onClick={() => setCfg({ ...cfg, host: '', name: '', line: '' })}>Revenir au casting par défaut</button>}
            <span className="qse-msg">{msg}</span>
          </div>
          <p className="qse-note">Script pour ta voix off : le texte de la bulle. Vise 10 à 20 secondes, en MP3 ou M4A.</p>
        </div>
      </div>
      {picking && <AvatarModal value={host} onPick={(v) => setCfg({ ...cfg, host: v })} onClose={() => setPicking(false)} />}
    </div>
  );
}

export default function QuizShowEditor({ api, onBack, initialFormat }) {
  const [fmt, setFmt] = useState(initialFormat || FORMATS[0].id);
  const [sets, setSets] = useState([]);
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState('');
  const F = BY_ID[fmt];

  const load = async () => { try { setSets((await api.get(`/sets?format=${fmt}`)).sets); } catch (e) { setMsg(e.message); } };
  useEffect(() => { setEdit(null); load(); }, [fmt]); // eslint-disable-line

  const warnings = edit && F.validate ? F.validate(edit.data || {}) : [];
  const save = async () => {
    setMsg('Enregistrement…');
    try {
      const body = { format: fmt, title: edit.title, theme: edit.theme, hint: edit.hint, data: edit.data };
      const { set } = edit._id ? await api.put(`/sets/${edit._id}`, body) : await api.post('/sets', body);
      setEdit(clone(set)); setMsg(warnings.length ? `Enregistré, mais ${warnings.length} point${warnings.length > 1 ? 's' : ''} à régler` : 'Enregistré ✓'); load();
    } catch (e) { setMsg(e.message); }
  };
  const remove = async (s) => { if (!window.confirm(`Supprimer « ${s.title || s.theme || 'sans titre'} » ?`)) return; await api.del(`/sets/${s._id}`); if (edit && edit._id === s._id) setEdit(null); load(); };
  const toggleUsed = async (s) => { await api.put(`/sets/${s._id}`, { ...s, status: s.status === 'used' ? 'ready' : 'used' }); load(); };
  const move = async (s, d) => {
    const ready = sets.filter((x) => x.status === 'ready');
    const k = ready.findIndex((x) => x._id === s._id); const o = ready[k + d];
    if (!o) return;
    await Promise.all([api.put(`/sets/${s._id}`, { ...s, order: o.order }), api.put(`/sets/${o._id}`, { ...o, order: s.order === o.order ? s.order + d : s.order })]);
    load();
  };
  const ready = sets.filter((s) => s.status === 'ready');

  return (
    <div className="qse">
      <header className="qse-top">
        <button type="button" className="qs-btn is-ghost" onClick={onBack}>← Retour</button>
        <h1>Éditeur des épreuves</h1>
        <span className="qse-msg">{msg}</span>
      </header>
      <nav className="qse-formats">
        {FORMATS.map((f) => (
          <button type="button" key={f.id} className={f.id === fmt ? 'is-on' : ''} onClick={() => setFmt(f.id)} style={{ '--c1': f.c1, '--c2': f.c2 }}>
            <span>{f.icon}</span>{f.name.join(' ')}
          </button>
        ))}
      </nav>
      <div className="qse-body">
        <aside className="qse-sets">
          <FormatLogo f={F} />
          <p className="qse-pitch">{F.pitch}</p>
          <button type="button" className="qs-btn is-gold" onClick={() => { setEdit({ title: '', theme: '', hint: '', data: F.blank() }); setMsg(''); }}>+ Nouveau contenu</button>
          <h3>File d’attente ({ready.length})</h3>
          <p className="qse-note">Le premier de la liste est celui qui sera joué. Une fois joué dans une partie terminée, il passe en « Joués ».</p>
          {sets.map((s) => (
            <div key={s._id} className={`qse-set${edit && edit._id === s._id ? ' is-on' : ''}${s.status === 'used' ? ' is-used' : ''}`}>
              <button type="button" className="qse-set-main" onClick={() => { setEdit(clone(s)); setMsg(''); }}>
                <b>{s.title || 'Sans titre'}</b><small>{s.theme || 'thème ?'}{s.revealed ? ' · indice révélé' : ''}{s.status === 'used' ? ' · joué' : ''}</small>
              </button>
              <div className="qse-set-ctl">
                {s.status === 'ready' && <><button type="button" title="Monter" onClick={() => move(s, -1)}>↑</button><button type="button" title="Descendre" onClick={() => move(s, 1)}>↓</button></>}
                <button type="button" title={s.status === 'used' ? 'Remettre en jeu' : 'Marquer comme joué'} onClick={() => toggleUsed(s)}>{s.status === 'used' ? '↺' : '✓'}</button>
                <button type="button" title="Supprimer" onClick={() => remove(s)}>🗑</button>
              </div>
            </div>
          ))}
        </aside>
        <section className="qse-form">
          <HostPanel api={api} fmt={fmt} />
          {!edit ? <div className="qse-empty">Choisis un contenu à gauche ou crée-en un nouveau.</div> : (
            <>
              <div className="qse-meta">
                <label><span className="qse-label">Titre (pour toi)</span><input value={edit.title || ''} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="ex. Starters toutes générations" /></label>
                <label><span className="qse-label">Thème (dévoilé au lancement)</span><input value={edit.theme || ''} onChange={(e) => setEdit({ ...edit, theme: e.target.value })} placeholder="ex. Pokémon, Dofus, Sonic, Animés…" /></label>
                <label><span className="qse-label">Indice du joker Radar (sinon le thème)</span><input value={edit.hint || ''} onChange={(e) => setEdit({ ...edit, hint: e.target.value })} placeholder="ex. Pokémon Unite" /></label>
              </div>
              <Group fields={F.schema} value={edit.data} root={edit.data} api={api} onChange={(d) => setEdit({ ...edit, data: d })} />
              {warnings.length > 0 && <ul className="qse-warn">{warnings.map((w) => <li key={w}>⚠️ {w}</li>)}</ul>}
              <div className="qse-save"><button type="button" className="qs-btn is-gold is-big" onClick={save}>Enregistrer</button><span>{msg}</span>{warnings.length > 0 && <span className="qse-warn-n">⚠️ {warnings.length} à régler avant de jouer</span>}</div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
